import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { 
  LoadBankAccounts, 
  CreateBankAccount,
  UpdateBankAccount,
  LoadCreditCards,
  CreateCreditCard,
  UpdateCreditCard,
  ClearPaymentMethodsState,
  PaymentMethodsState
} from '../states/payment-methods.state';
import { BankAccount, CreditCard, LocalStorageService } from '@ops/shared-types';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodsManagerService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private lastLoadTime: number = 0;
  private currentCompanyId: string | null = null;

  constructor(
    private store: Store,
    private localStorageService: LocalStorageService
  ) {}

  // Bank Account Methods
  loadBankAccounts(companyId: string, forceRefresh: boolean = false): Observable<BankAccount[]> {
    const currentTime = Date.now();
    const state = this.store.selectSnapshot(PaymentMethodsState.getBankAccounts);
    const lastUpdated = this.store.selectSnapshot(PaymentMethodsState.getLastUpdated);
    
    // Check if we need to refresh data
    if (!forceRefresh && 
        state.length > 0 && 
        this.currentCompanyId === companyId && 
        lastUpdated && 
        (currentTime - lastUpdated.getTime()) < this.CACHE_DURATION) {
      return this.store.select(PaymentMethodsState.getBankAccounts);
    }

    this.currentCompanyId = companyId;
    this.store.dispatch(new LoadBankAccounts(companyId));
    return this.store.select(PaymentMethodsState.getBankAccounts);
  }

  createBankAccount(bankAccount: Partial<BankAccount>, companyId: string): Observable<BankAccount> {
    this.store.dispatch(new CreateBankAccount(bankAccount, companyId));
    
    // Return the created bank account from state
    return new Observable(observer => {
      const subscription = this.store.select(PaymentMethodsState.getBankAccounts).subscribe(accounts => {
        const newAccount = accounts.find(account => 
          account.shortName === bankAccount.shortName && 
          account.company === companyId
        );
        if (newAccount) {
          observer.next(newAccount);
          observer.complete();
        }
      });
      
      return () => subscription.unsubscribe();
    });
  }

  updateBankAccount(bankAccount: BankAccount, companyId: string): Observable<BankAccount> {
    this.store.dispatch(new UpdateBankAccount(bankAccount, companyId));
    
    // Return the updated bank account from state
    return new Observable(observer => {
      const subscription = this.store.select(PaymentMethodsState.getBankAccounts).subscribe(accounts => {
        const updatedAccount = accounts.find(account => account._id === bankAccount._id);
        if (updatedAccount) {
          observer.next(updatedAccount);
          observer.complete();
        }
      });
      
      return () => subscription.unsubscribe();
    });
  }

  // Credit Card Methods
  loadCreditCards(companyId: string, forceRefresh: boolean = false): Observable<CreditCard[]> {
    const currentTime = Date.now();
    const state = this.store.selectSnapshot(PaymentMethodsState.getCreditCards);
    const lastUpdated = this.store.selectSnapshot(PaymentMethodsState.getLastUpdated);
    
    // Check if we need to refresh data
    if (!forceRefresh && 
        state.length > 0 && 
        this.currentCompanyId === companyId && 
        lastUpdated && 
        (currentTime - lastUpdated.getTime()) < this.CACHE_DURATION) {
      return this.store.select(PaymentMethodsState.getCreditCards);
    }

    this.currentCompanyId = companyId;
    this.store.dispatch(new LoadCreditCards(companyId));
    return this.store.select(PaymentMethodsState.getCreditCards);
  }

  createCreditCard(creditCard: Partial<CreditCard>, companyId: string): Observable<CreditCard> {
    this.store.dispatch(new CreateCreditCard(creditCard, companyId));
    
    // Return the created credit card from state
    return new Observable(observer => {
      const subscription = this.store.select(PaymentMethodsState.getCreditCards).subscribe(cards => {
        const newCard = cards.find(card => 
          card.shortName === creditCard.shortName && 
          card.company === companyId
        );
        if (newCard) {
          observer.next(newCard);
          observer.complete();
        }
      });
      
      return () => subscription.unsubscribe();
    });
  }

  updateCreditCard(creditCard: CreditCard, companyId: string): Observable<CreditCard> {
    this.store.dispatch(new UpdateCreditCard(creditCard, companyId));
    
    // Return the updated credit card from state
    return new Observable(observer => {
      const subscription = this.store.select(PaymentMethodsState.getCreditCards).subscribe(cards => {
        const updatedCard = cards.find(card => card._id === creditCard._id);
        if (updatedCard) {
          observer.next(updatedCard);
          observer.complete();
        }
      });
      
      return () => subscription.unsubscribe();
    });
  }

  // Utility Methods
  getActiveBankAccounts(): Observable<BankAccount[]> {
    return this.store.select(PaymentMethodsState.getActiveBankAccounts);
  }

  getActiveCreditCards(): Observable<CreditCard[]> {
    return this.store.select(PaymentMethodsState.getActiveCreditCards);
  }

  getFavoriteBankAccounts(): Observable<BankAccount[]> {
    return this.store.select(PaymentMethodsState.getFavoriteBankAccounts);
  }

  getFavoriteCreditCards(): Observable<CreditCard[]> {
    return this.store.select(PaymentMethodsState.getFavoriteCreditCards);
  }

  getBankAccountsByCurrency(currencyCode: string): Observable<BankAccount[]> {
    return this.store.select(PaymentMethodsState.getBankAccountsByCurrency).pipe(
      map(selector => selector(currencyCode))
    );
  }

  getCreditCardsByBankAccount(bankAccountId: string): Observable<CreditCard[]> {
    return this.store.select(PaymentMethodsState.getCreditCardsByBankAccount).pipe(
      map(selector => selector(bankAccountId))
    );
  }

  isLoading(): Observable<boolean> {
    return this.store.select(PaymentMethodsState.isLoading);
  }

  getError(): Observable<string | null> {
    return this.store.select(PaymentMethodsState.getError);
  }

  hasData(): Observable<boolean> {
    return this.store.select(PaymentMethodsState.hasData);
  }

  clearState(): void {
    this.store.dispatch(new ClearPaymentMethodsState());
    this.currentCompanyId = null;
    this.lastLoadTime = 0;
  }

  // Load all payment methods for a company
  loadAllPaymentMethods(companyId: string, forceRefresh: boolean = false): Observable<{bankAccounts: BankAccount[], creditCards: CreditCard[]}> {
    const bankAccounts$ = this.loadBankAccounts(companyId, forceRefresh);
    const creditCards$ = this.loadCreditCards(companyId, forceRefresh);
    
    return new Observable(observer => {
      let bankAccounts: BankAccount[] = [];
      let creditCards: CreditCard[] = [];
      let bankAccountsLoaded = false;
      let creditCardsLoaded = false;

      const checkComplete = () => {
        if (bankAccountsLoaded && creditCardsLoaded) {
          observer.next({ bankAccounts, creditCards });
          observer.complete();
        }
      };

      const bankSubscription = bankAccounts$.subscribe(accounts => {
        bankAccounts = accounts;
        bankAccountsLoaded = true;
        checkComplete();
      });

      const creditSubscription = creditCards$.subscribe(cards => {
        creditCards = cards;
        creditCardsLoaded = true;
        checkComplete();
      });

      return () => {
        bankSubscription.unsubscribe();
        creditSubscription.unsubscribe();
      };
    });
  }

  // Get payment methods for dropdowns (formatted for UI)
  getPaymentMethodsForDropdowns(companyId: string): Observable<{
    bankAccounts: Array<{key: string, value: string}>,
    creditCards: Array<{key: string, value: string}>
  }> {
    return new Observable(observer => {
      const subscription = this.loadAllPaymentMethods(companyId).subscribe(({bankAccounts, creditCards}) => {
        const formattedBankAccounts = bankAccounts
          .filter(account => !account.isDebit)
          .filter(account => !['DEBIT', 'CASH', 'TEMP'].includes(account.bankName?.toUpperCase() || ''))
          .filter(account => !['DEBIT', 'CASH', 'TEMP'].includes(account.branchName?.toUpperCase() || ''))
          .map(account => ({
            key: account._id,
            value: account.shortName
          }));

        const formattedCreditCards = creditCards
          .filter(card => card.isActive)
          .map(card => ({
            key: card._id,
            value: card.shortName
          }));

        observer.next({
          bankAccounts: formattedBankAccounts,
          creditCards: formattedCreditCards as Array<{key: string, value: string}>
        });
        observer.complete();
      });

      return () => subscription.unsubscribe();
    });
  }
}
